import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  DashboardTable,
  DashboardTableBody,
  DashboardTableHeader,
  DashboardTableRow,
  PillTabs,
  TableChip,
} from '../../components/PremiumDesign';
import { TemplatePhonePreview } from '../../components/TemplatePhonePreview';
import {
  Button,
  ConfirmDeleteModal,
  CustomSelect,
  Modal,
  Pagination,
  Switch,
} from '@/components';
import { V2Table } from '@/common/components';
import { PlusIcon } from '../../components/Icons';
import { StopFollowUpConfirmModal } from '../../components/StopFollowUpConfirmModal';
import editIcon from '../../assets/editIcon.png';
import trashIcon from '../../assets/trashIcon.png';
import pauseCircleIcon from '../../assets/leads/pauseCircle.png';
import { parseApiError, formatDateTime } from '@/common/utils';
import { uploadWhatsAppFlowMedia } from '../../whatsapp-flows/api';
import {
  cancelStageAutomationExecution,
  createStageAutomation,
  deleteStageAutomation,
  getCRMPipelineStages,
  getWhatsappTemplates,
  confirmApplyExisting,
  listStageAutomationLogs,
  listStageAutomations,
  previewApplyExisting,
  toggleStageAutomation,
  updateStageAutomation,
} from '../api';

const DELAY_UNITS = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
];

const QUICK_DELAYS = [
  { label: '5 min', value: '5', unit: 'minutes' },
  { label: '15 min', value: '15', unit: 'minutes' },
  { label: '30 min', value: '30', unit: 'minutes' },
  { label: '1 hour', value: '1', unit: 'hours' },
  { label: '4 hours', value: '4', unit: 'hours' },
  { label: '1 day', value: '1', unit: 'days' },
];

const LOG_STATUSES = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'sent', label: 'Sent' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'failed', label: 'Failed' },
];

const STATUS_TONE = {
  pending: 'warning',
  sent: 'success',
  cancelled: 'neutral',
  skipped: 'neutral',
  failed: 'error',
};

const TOKEN_OPTIONS = [
  { token: '{{contact.name}}', label: 'Contact name', sample: 'Alex Morgan' },
  { token: '{{contact.phone}}', label: 'Phone', sample: '+1 (555) 019-2834' },
  {
    token: '{{contact.email}}',
    label: 'Email',
    sample: 'alex.morgan@example.com',
  },
  { token: '{{company.name}}', label: 'Company', sample: 'Acme Global' },
  { token: '{{stage.name}}', label: 'Stage', sample: 'Qualified Lead' },
];

function asList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

function bodyPlaceholderKeys(template) {
  const body = template?.components?.find((c) => c.type === 'BODY');
  const text = body?.text || template?.body_text || '';
  const keys = [];
  const re = /\{\{(\d+)\}\}/g;
  let match;
  while ((match = re.exec(text)) !== null) {
    if (!keys.includes(match[1])) keys.push(match[1]);
  }
  return keys;
}

function headerMediaKind(template) {
  const header = template?.components?.find((c) => c.type === 'HEADER');
  const format = String(header?.format || '').toUpperCase();
  if (format === 'IMAGE' || format === 'VIDEO' || format === 'DOCUMENT') {
    return format.toLowerCase();
  }
  return 'file';
}

function fileNameFromUrl(url) {
  if (!url) return '';
  try {
    const name = decodeURIComponent(
      new URL(url).pathname.split('/').pop() || '',
    );
    return name || 'Uploaded file';
  } catch {
    return 'Uploaded file';
  }
}

function hasMediaHeader(template) {
  const header = template?.components?.find((c) => c.type === 'HEADER');
  const format = String(header?.format || '').toUpperCase();
  if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(format)) return true;
  const cards =
    template?.cards ||
    template?.components?.find((c) => c.type === 'CAROUSEL')?.cards ||
    [];
  return cards.some((card) => {
    const cardHeader = card.components?.find((c) => c.type === 'HEADER');
    const cardFormat = String(
      cardHeader?.format || card.header_format || '',
    ).toUpperCase();
    return ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(cardFormat);
  });
}

function formatDelay(rule) {
  const value = Number(rule?.delay_value ?? 0);
  if (!value) return 'Send immediately';
  const unit = rule.delay_unit || 'minutes';
  const label = value === 1 ? unit.replace(/s$/, '') : unit;
  return `${value} ${label}`;
}

function formatWhen(value) {
  return formatDateTime(value);
}

function logReason(row) {
  if (row.status === 'failed') return row.error_message || '—';
  if (row.status === 'cancelled' || row.status === 'skipped') {
    return row.cancel_reason || row.error_message || '—';
  }
  return '—';
}

function WhatsAppIcon({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.888-.788-1.487-1.761-1.66-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function ApplyExistingBody({
  preview,
  stageName,
  isActive,
  notice,
  running,
  pending,
  onSkip,
  onSend,
}) {
  const eligible = Number(preview.eligible) || 0;
  const noPhone = Number(preview.skipped_no_phone) || 0;
  const optedOut = Number(preview.skipped_opt_out) || 0;
  const alreadySent = Number(preview.already_sent) || 0;
  const canSend = isActive && eligible > 0 && !running && !pending;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-text-secondary">
        {eligible} leads already in {stageName}. {noPhone} have no phone.{' '}
        {optedOut} opted out. {alreadySent} already sent.
      </p>
      <p className="text-xs text-text-muted">
        Saving a rule does not message leads already in this stage. The delay
        starts at this confirm, then about 1 second between leads. Leads already
        sent for this rule are not sent again. A lead who leaves and later
        re-enters the stage does get a new send.
      </p>
      {running && (
        <p className="text-sm font-medium text-primary">Send in progress</p>
      )}
      {!running && notice && (
        <p className="text-sm font-medium text-primary">{notice}</p>
      )}
      {!isActive && (
        <p className="text-sm text-status-error">Turn the rule on first.</p>
      )}
      <div className="flex flex-col gap-2">
        {!running && (
          <Button
            variant="primary"
            className="h-11 w-full"
            disabled={!canSend}
            onClick={onSend}
          >
            {pending ? 'Queuing...' : `Also send to these ${eligible}`}
          </Button>
        )}
        <Button
          variant="secondary"
          className="h-11 w-full"
          disabled={pending}
          onClick={onSkip}
        >
          Only new leads who enter this stage
        </Button>
      </div>
    </div>
  );
}

export function StageAutomationsTab({
  companyId,
  isReadOnly = false,
  canCreate = false,
  canUpdate = false,
  canDelete = false,
  tabs,
  activeTab,
  onTabChange,
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const contactIdFromLead = searchParams.get('contact_id') || '';
  const logFromUrl = searchParams.get('log') || '';

  const [view, setView] = useState(() =>
    searchParams.get('view') === 'logs' && logFromUrl ? 'logs' : 'list',
  );
  const [editing, setEditing] = useState(null);
  const [stageId, setStageId] = useState('');
  const [templateKey, setTemplateKey] = useState('');
  const [timingMode, setTimingMode] = useState('immediate'); // 'immediate' | 'delay'
  const [delayValue, setDelayValue] = useState('0');
  const [delayUnit, setDelayUnit] = useState('minutes');
  const [variables, setVariables] = useState({});
  const [headerMediaUrl, setHeaderMediaUrl] = useState('');
  const [headerMediaName, setHeaderMediaName] = useState('');
  const [isUploadingHeader, setIsUploadingHeader] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [apiErrors, setApiErrors] = useState({});
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pendingToggleOff, setPendingToggleOff] = useState(null);
  const [pendingCancel, setPendingCancel] = useState(null);
  const [applyRule, setApplyRule] = useState(null);
  const [applyNotice, setApplyNotice] = useState('');
  const [applyRunning, setApplyRunning] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileTab, setMobileTab] = useState('form'); // 'form' | 'preview'

  const [logAutomationId, setLogAutomationId] = useState(logFromUrl || null);

  const syncLogsRoute = (nextView, automationId) => {
    const next = new URLSearchParams(searchParams);
    if (!next.get('tab')) next.set('tab', 'automations');
    if (nextView === 'logs' && automationId) {
      next.set('view', 'logs');
      next.set('log', String(automationId));
    } else {
      next.delete('view');
      next.delete('log');
    }
    setSearchParams(next, { replace: true });
  };

  const showList = () => {
    setView('list');
    syncLogsRoute('list');
  };
  const [logPage, setLogPage] = useState(1);
  const [logStatus, setLogStatus] = useState('');
  const [logStageId, setLogStageId] = useState('');

  const { data: stagesData, isLoading: stagesLoading } = useQuery({
    queryKey: ['crm-pipeline-stages', companyId],
    queryFn: () => getCRMPipelineStages(companyId),
    enabled: !!companyId,
  });
  const stages = asList(stagesData);

  const { data: automationsData, isLoading: automationsLoading } = useQuery({
    queryKey: ['stage-automations', companyId],
    queryFn: () => listStageAutomations(companyId),
    enabled: !!companyId,
  });
  const automations = asList(automationsData);

  const { data: templatesData, isLoading: templatesLoading } = useQuery({
    queryKey: ['whatsapp-templates', companyId],
    queryFn: () => getWhatsappTemplates(companyId),
    enabled: !!companyId,
  });
  const approvedTemplates = useMemo(
    () =>
      asList(templatesData).filter(
        (template) =>
          String(template.status || '').toUpperCase() === 'APPROVED',
      ),
    [templatesData],
  );

  const selectedTemplate = approvedTemplates.find(
    (template) => template.name === templateKey,
  );
  const placeholderKeys = useMemo(
    () => (selectedTemplate ? bodyPlaceholderKeys(selectedTemplate) : []),
    [selectedTemplate],
  );
  const placeholderKeysStr = placeholderKeys.join('|');
  const showHeaderUrl = selectedTemplate
    ? hasMediaHeader(selectedTemplate)
    : Boolean(headerMediaUrl);

  const rows = useMemo(() => {
    const byStage = new Map(
      automations.map((rule) => [String(rule.stage_id), rule]),
    );
    const ordered = [...stages].sort(
      (a, b) =>
        (a.display_order ?? a.order ?? 0) - (b.display_order ?? b.order ?? 0),
    );
    return ordered.map((stage) => ({
      stage,
      rule: byStage.get(String(stage.id)) || null,
    }));
  }, [stages, automations]);

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows;
    const q = searchQuery.toLowerCase().trim();
    return rows.filter(
      ({ stage, rule }) =>
        stage.name?.toLowerCase().includes(q) ||
        rule?.template_name?.toLowerCase().includes(q),
    );
  }, [rows, searchQuery]);

  const activeAutomationsCount = useMemo(
    () => automations.filter((a) => a.is_active).length,
    [automations],
  );

  const usedStageIds = new Set(
    automations.map((rule) => String(rule.stage_id)),
  );
  const createStageOptions = stages
    .filter((stage) => !usedStageIds.has(String(stage.id)))
    .map((stage) => ({ value: String(stage.id), label: stage.name }));

  const templateOptions = approvedTemplates.map((template) => ({
    value: template.name,
    label: template.name,
  }));

  const logParams = {
    page: logPage,
    page_size: 10,
    ...(logStatus ? { status: logStatus } : {}),
    ...(!logAutomationId && logStageId ? { stage_id: logStageId } : {}),
    ...(!logAutomationId && contactIdFromLead
      ? { contact_id: contactIdFromLead }
      : {}),
  };

  const { data: logsData, isLoading: logsLoading } = useQuery({
    queryKey: ['stage-automation-logs', companyId, logAutomationId, logParams],
    queryFn: () =>
      listStageAutomationLogs({
        companyId,
        automationId: logAutomationId,
        params: logParams,
      }),
    enabled: !!companyId && view === 'logs',
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const results = query.state.data?.results || [];
      return results.some((row) => row.status === 'pending') ? 5000 : false;
    },
  });

  useEffect(() => {
    if (!selectedTemplate) return;
    setVariables((current) => {
      const next = {};
      placeholderKeys.forEach((key) => {
        next[key] = current[key] || '';
      });
      return next;
    });
  }, [selectedTemplate, placeholderKeys, placeholderKeysStr]);

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: ['stage-automations', companyId],
    });
  };

  const saveMutation = useMutation({
    mutationFn: (payload) => {
      if (editing) {
        if (!canUpdate) throw new Error('Permission denied.');
        return updateStageAutomation({
          companyId,
          automationId: editing.id,
          payload,
        });
      }
      if (!canCreate) throw new Error('Permission denied.');
      return createStageAutomation({ companyId, payload });
    },
    onSuccess: (saved) => {
      const wasEdit = Boolean(editing);
      toast.success(
        wasEdit
          ? 'Automation updated successfully.'
          : 'Automation created successfully.',
      );
      invalidate();
      showList();
      setEditing(null);
      const rule = saved?.id ? saved : null;
      if (!wasEdit && rule) {
        const stageName =
          rule.stage_name ||
          stages.find((stage) => String(stage.id) === String(rule.stage_id))
            ?.name ||
          '';
        setApplyNotice('');
        setApplyRunning(false);
        setApplyRule({
          id: rule.id,
          stage_name: stageName,
          is_active: rule.is_active !== false,
        });
      }
    },
    onError: (err) => {
      const { message, fieldErrors } = parseApiError(err);
      setApiErrors(fieldErrors || {});
      toast.error(message || 'Could not save automation.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (automationId) => {
      if (!canDelete) throw new Error('Permission denied.');
      return deleteStageAutomation({ companyId, automationId });
    },
    onSuccess: () => {
      toast.success('Automation deleted. Queued messages were cancelled.');
      invalidate();
      setPendingDelete(null);
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Could not delete automation.');
      setPendingDelete(null);
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ automationId, nextActive }) => {
      if (!canUpdate) throw new Error('Permission denied.');
      return toggleStageAutomation({
        companyId,
        automationId,
        isActive: nextActive,
      });
    },
    onSuccess: () => {
      invalidate();
      setPendingToggleOff(null);
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Could not update automation.');
      setPendingToggleOff(null);
    },
  });

  const openCreate = (preselectedStageId = '') => {
    setEditing(null);
    setStageId(preselectedStageId ? String(preselectedStageId) : '');
    setTemplateKey('');
    setTimingMode('immediate');
    setDelayValue('0');
    setDelayUnit('minutes');
    setVariables({});
    setHeaderMediaUrl('');
    setHeaderMediaName('');
    setIsActive(true);
    setApiErrors({});
    setMobileTab('form');
    setView('form');
  };

  const openEdit = (rule) => {
    setEditing(rule);
    setStageId(String(rule.stage_id));
    setTemplateKey(rule.template_name || '');
    const numDelay = Number(rule.delay_value ?? 0);
    if (numDelay > 0) {
      setTimingMode('delay');
      setDelayValue(String(numDelay));
      setDelayUnit(rule.delay_unit || 'minutes');
    } else {
      setTimingMode('immediate');
      setDelayValue('0');
      setDelayUnit('minutes');
    }
    setVariables({ ...(rule.template_variables || {}) });
    setHeaderMediaUrl(rule.header_media_url || '');
    setHeaderMediaName(fileNameFromUrl(rule.header_media_url || ''));
    setIsActive(Boolean(rule.is_active));
    setApiErrors({});
    setMobileTab('form');
    setView('form');
  };

  const openLogs = (rule) => {
    setLogAutomationId(rule?.id || null);
    setLogStageId(rule?.stage_id ? String(rule.stage_id) : '');
    setLogStatus('');
    setLogPage(1);
    setView('logs');
    syncLogsRoute('logs', rule?.id);
  };

  const {
    data: applyPreview,
    isLoading: applyPreviewLoading,
    error: applyPreviewError,
  } = useQuery({
    queryKey: ['stage-automation-apply', companyId, applyRule?.id],
    queryFn: () =>
      previewApplyExisting({ companyId, automationId: applyRule.id }),
    enabled: !!companyId && !!applyRule?.id,
  });

  const cancelMutation = useMutation({
    mutationFn: (executionId) =>
      cancelStageAutomationExecution({ companyId, executionId }),
    onSuccess: (updated) => {
      queryClient.setQueriesData(
        { queryKey: ['stage-automation-logs', companyId] },
        (old) => {
          if (!old?.results) return old;
          return {
            ...old,
            results: old.results.map((row) =>
              String(row.id) === String(updated?.id)
                ? { ...row, ...updated }
                : row,
            ),
          };
        },
      );
      setPendingCancel(null);
      toast.success('Scheduled send cancelled.');
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Could not cancel send.');
      setPendingCancel(null);
    },
  });

  const applyMutation = useMutation({
    mutationFn: () =>
      confirmApplyExisting({ companyId, automationId: applyRule.id }),
    onSuccess: ({ status, data }) => {
      if (data?.already_running) {
        setApplyRunning(true);
        setApplyNotice('Send in progress');
        return;
      }
      if (status === 202 || data?.queued) {
        toast.success('Queued. The delay starts now.');
        const rule = applyRule;
        setApplyRule(null);
        openLogs(rule);
        return;
      }
      setApplyNotice('Nothing new to send.');
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Could not queue existing leads.');
    },
  });

  const handleSave = () => {
    const template = selectedTemplate || null;
    const templateName = template?.name || templateKey;
    const cleanedVariables = {};
    Object.entries(variables).forEach(([key, value]) => {
      if (String(value || '').trim())
        cleanedVariables[key] = String(value).trim();
    });

    const parsedDelay =
      timingMode === 'immediate'
        ? 0
        : Math.max(0, Number.parseInt(delayValue, 10) || 0);

    const payload = {
      template_name: templateName,
      template_language:
        template?.language || editing?.template_language || 'en_US',
      template_variables: cleanedVariables,
      header_media_url: headerMediaUrl.trim(),
      delay_value: parsedDelay,
      delay_unit: delayUnit,
      is_active: isActive,
    };
    if (template?.id) payload.template_id = String(template.id);
    if (!editing) payload.stage_id = Number(stageId);
    setApiErrors({});
    saveMutation.mutate(payload);
  };

  const fieldError = (key) => {
    const value = apiErrors?.[key];
    if (!value) return '';
    return typeof value === 'string' ? value : String(value);
  };

  // Map variables {key: val} → TemplatePhonePreview's templateValues format {body_var_key: val}
  const templateValues = useMemo(() => {
    const result = {};
    Object.entries(variables).forEach(([key, val]) => {
      if (val) result[`body_var_${key}`] = val;
    });
    return result;
  }, [variables]);

  const pageTitle =
    view === 'form'
      ? editing
        ? 'Edit Stage Automation'
        : 'Create Stage Automation'
      : view === 'logs'
        ? 'Automation Send Logs'
        : 'Inquiries Stage Triggers';

  const pageHeader = (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 shrink-0 mb-4">
      <div className="flex items-center gap-3">
        {view !== 'list' && (
          <button
            type="button"
            onClick={showList}
            className="w-8 h-8 rounded-lg flex items-center justify-center border border-border-default hover:bg-surface-mint transition-colors text-text-primary"
            title="Back to Stage Triggers"
            aria-label="Back to Stage Triggers"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        )}
        <div>
          <h4 className="text-[20px] sm:text-[22px] leading-[28px] font-bold font-sf-pro-display text-[#1F2241]">
            {pageTitle}
          </h4>
        </div>
      </div>
      {tabs && (
        <PillTabs items={tabs} value={activeTab} onChange={onTabChange} />
      )}
    </div>
  );

  // ----------------------------------------------------
  // VIEW: FORM (CREATE / EDIT AUTOMATION)
  // ----------------------------------------------------
  if (view === 'form') {
    return (
      <div className="pipeline-automation-form flex flex-col flex-1 min-h-0">
        {pageHeader}

        {/* Mobile View Toggle Switcher (< lg) */}
        <div className="flex lg:hidden items-center p-1 bg-form-background-light rounded-xl border border-border-default mb-4 shrink-0">
          <button
            type="button"
            onClick={() => setMobileTab('form')}
            className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all ${
              mobileTab === 'form'
                ? 'bg-white text-primary shadow-sm'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            Configure automation
          </button>
          <button
            type="button"
            onClick={() => setMobileTab('preview')}
            className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all flex items-center justify-center gap-1.5 ${
              mobileTab === 'preview'
                ? 'bg-white text-primary shadow-sm'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            <WhatsAppIcon className="w-3.5 h-3.5 text-primary" />
            <span>Message Preview</span>
            {placeholderKeys.length > 0 && (
              <span className="w-4 h-4 rounded-full bg-surface-mint text-primary text-[10px] flex items-center justify-center">
                {placeholderKeys.length}
              </span>
            )}
          </button>
        </div>

        {/* 2-Column Responsive Workspace */}
        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-1">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            {/* LEFT COLUMN: Configuration Form */}
            <div
              className={`flex flex-col gap-5 ${mobileTab === 'preview' ? 'hidden lg:flex' : 'flex'} lg:col-span-7 xl:col-span-7`}
            >
              {/* SECTION 1: Trigger & Template */}
              <div className="bg-white rounded-2xl border border-border-default p-5 shadow-sm flex flex-col gap-4">
                <div className="flex items-center gap-2 pb-3 border-b border-border-default">
                  <div className="w-7 h-7 rounded-lg bg-surface-mint flex items-center justify-center text-primary">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h5 className="text-sm font-bold text-text-primary">
                      Trigger & Template
                    </h5>
                  </div>
                </div>

                {/* Stage Selector */}
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center">
                    <label className="text-xs font-semibold text-text-primary">
                      Inquiries Stage{' '}
                      <span className="text-status-error">*</span>
                    </label>
                  </div>
                  {editing ? (
                    <div className="h-11 px-3.5 rounded-xl bg-form-background-light border border-border-default flex items-center justify-between text-sm text-text-primary font-medium opacity-85">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-primary" />
                        {editing.stage_name || 'Stage'}
                      </span>
                      <span className="text-xs text-text-muted flex items-center gap-1 font-normal">
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                          />
                        </svg>
                        Stage locked in edit
                      </span>
                    </div>
                  ) : (
                    <>
                      <CustomSelect
                        value={stageId}
                        onChange={(val) => {
                          setStageId(val);
                          setApiErrors((prev) => ({ ...prev, stage_id: null }));
                        }}
                        options={createStageOptions}
                        placeholder={
                          createStageOptions.length === 0
                            ? 'All inquires stages already have triggers'
                            : 'Select an inquires stage to trigger this message'
                        }
                        searchable
                        disabled={
                          createStageOptions.length === 0 ||
                          templatesLoading ||
                          saveMutation.isPending
                        }
                        className="w-full h-11"
                        buttonClassName="w-full h-11 flex items-center justify-between px-3.5 rounded-xl bg-white border border-border-default text-text-primary text-sm font-medium focus:border-primary transition-all"
                        textSizeClass="text-sm"
                      />
                      {createStageOptions.length === 0 && (
                        <p className="text-xs text-status-warning font-medium">
                          All stages are already configured. Edit an existing
                          rule from the list to update it.
                        </p>
                      )}
                    </>
                  )}
                  {fieldError('stage_id') && (
                    <p className="text-status-error text-xs font-medium mt-0.5">
                      {fieldError('stage_id')}
                    </p>
                  )}
                </div>

                {/* WhatsApp Template Selector */}
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-text-primary">
                      Approved WhatsApp Template{' '}
                      <span className="text-status-error">*</span>
                    </label>
                  </div>
                  <CustomSelect
                    value={templateKey}
                    onChange={(value) => {
                      setTemplateKey(value);
                      setApiErrors((prev) => ({
                        ...prev,
                        template_name: null,
                      }));
                    }}
                    options={templateOptions}
                    placeholder={
                      templatesLoading
                        ? 'Loading templates...'
                        : approvedTemplates.length === 0
                          ? 'No approved templates found'
                          : 'Search and select an approved template'
                    }
                    searchable
                    disabled={templatesLoading || saveMutation.isPending}
                    className="w-full h-11"
                    buttonClassName="w-full h-11 flex items-center justify-between px-3.5 rounded-xl bg-white border border-border-default text-text-primary text-sm font-medium focus:border-primary transition-all"
                    textSizeClass="text-sm"
                  />
                  {fieldError('template_name') && (
                    <p className="text-status-error text-xs font-medium mt-0.5">
                      {fieldError('template_name')}
                    </p>
                  )}
                </div>
              </div>

              {/* SECTION 2: Delivery Timing */}
              <div className="bg-white rounded-2xl border border-border-default p-5 shadow-sm flex flex-col gap-4">
                <div className="flex items-center gap-2 pb-3 border-b border-border-default">
                  <div className="w-7 h-7 rounded-lg bg-surface-mint flex items-center justify-center text-primary">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h5 className="text-sm font-bold text-text-primary">
                      Delivery Timing
                    </h5>
                  </div>
                </div>

                {/* Timing Mode Segmented Tabs */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setTimingMode('immediate');
                      setDelayValue('0');
                    }}
                    className={`p-3 rounded-xl border text-left transition-all flex flex-col gap-1 ${
                      timingMode === 'immediate'
                        ? 'border-primary bg-surface-mint/50 ring-1 ring-primary'
                        : 'border-border-default bg-white hover:border-border-default/80 hover:bg-form-background-light'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-text-primary flex items-center gap-1.5">
                        Send immediately
                      </span>
                      {timingMode === 'immediate' && (
                        <span className="w-2 h-2 rounded-full bg-primary" />
                      )}
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setTimingMode('delay');
                      if (delayValue === '0') setDelayValue('15');
                    }}
                    className={`p-3 rounded-xl border text-left transition-all flex flex-col gap-1 ${
                      timingMode === 'delay'
                        ? 'border-primary bg-surface-mint/50 ring-1 ring-primary'
                        : 'border-border-default bg-white hover:border-border-default/80 hover:bg-form-background-light'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-text-primary flex items-center gap-1.5">
                        Wait before sending
                      </span>
                      {timingMode === 'delay' && (
                        <span className="w-2 h-2 rounded-full bg-primary" />
                      )}
                    </div>
                  </button>
                </div>

                {/* Delay Controls (When delay is selected) */}
                {timingMode === 'delay' && (
                  <div className="flex flex-col gap-3 p-3.5 rounded-xl bg-form-background-light border border-border-default animate-fadeIn">
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                      <div className="flex-1">
                        <label className="text-[11px] font-semibold text-text-secondary block mb-1">
                          Delay Duration
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="999"
                          value={delayValue}
                          onChange={(e) => setDelayValue(e.target.value)}
                          placeholder="e.g. 15"
                          className="w-full h-11 px-3.5 rounded-xl bg-white border border-border-default text-text-primary text-sm font-medium focus:border-primary focus:outline-none transition-colors"
                        />
                      </div>
                      <div className="w-full sm:w-44">
                        <label className="text-[11px] font-semibold text-text-secondary block mb-1">
                          Time Unit
                        </label>
                        <CustomSelect
                          value={delayUnit}
                          onChange={setDelayUnit}
                          options={DELAY_UNITS}
                          className="w-full h-11"
                          buttonClassName="w-full h-11 flex items-center justify-between px-3.5 rounded-xl bg-white border border-border-default text-text-primary text-sm font-medium focus:border-primary transition-all"
                          textSizeClass="text-sm"
                        />
                      </div>
                    </div>

                    {/* Quick Presets */}
                    <div>
                      <span className="text-[11px] font-medium text-text-muted block mb-1.5">
                        Quick Presets:
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {QUICK_DELAYS.map((qd) => {
                          const isSelected =
                            delayValue === qd.value && delayUnit === qd.unit;
                          return (
                            <button
                              key={qd.label}
                              type="button"
                              onClick={() => {
                                setDelayValue(qd.value);
                                setDelayUnit(qd.unit);
                              }}
                              className={`px-3 py-1 rounded-lg text-xs font-medium border transition-colors ${
                                isSelected
                                  ? 'bg-surface-mint border-primary text-primary font-semibold'
                                  : 'bg-white border-border-default text-text-secondary hover:border-primary hover:text-text-primary'
                              }`}
                            >
                              {qd.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* SECTION 3: Template Variables Mapping */}
              {placeholderKeys.length > 0 && (
                <div className="bg-white rounded-2xl border border-border-default p-5 shadow-sm flex flex-col gap-4">
                  <div className="flex items-center justify-between pb-3 border-b border-border-default">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg bg-surface-mint flex items-center justify-center text-primary">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                          />
                        </svg>
                      </div>
                      <div>
                        <h5 className="text-sm font-bold text-text-primary">
                          Variable Mapping
                        </h5>
                      </div>
                    </div>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-surface-mint text-primary">
                      {placeholderKeys.length}{' '}
                      {placeholderKeys.length === 1 ? 'Field' : 'Fields'}
                    </span>
                  </div>

                  <div className="flex flex-col gap-4">
                    {placeholderKeys.map((key) => {
                      const currentVal = variables[key] || '';

                      return (
                        <div
                          key={key}
                          className="p-4 rounded-xl border border-border-default bg-[#FAFBF9] flex flex-col gap-2.5 transition-all focus-within:border-primary"
                        >
                          {/* Variable Label & Context Snippet */}
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5">
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-0.5 rounded-md bg-surface-mint border border-primary/30 font-mono text-xs font-bold text-primary">
                                {`{{${key}}}`}
                              </span>
                              <span className="text-xs font-semibold text-text-primary">
                                Variable Placeholder {key}
                              </span>
                            </div>
                          </div>

                          {/* Input field */}
                          <div className="relative">
                            <input
                              type="text"
                              value={currentVal}
                              onChange={(e) =>
                                setVariables((current) => ({
                                  ...current,
                                  [key]: e.target.value,
                                }))
                              }
                              placeholder={
                                key === '1'
                                  ? 'Default: Lead Contact Name'
                                  : key === '2'
                                    ? 'Default: Company Name'
                                    : 'Select a field below or enter custom text'
                              }
                              className="w-full h-11 px-3.5 pr-8 rounded-xl bg-white border border-border-default text-text-primary text-sm placeholder:text-text-muted/70 focus:outline-none focus:border-primary transition-colors"
                            />
                            {currentVal && (
                              <button
                                type="button"
                                onClick={() =>
                                  setVariables((current) => ({
                                    ...current,
                                    [key]: '',
                                  }))
                                }
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full text-text-muted hover:text-text-primary flex items-center justify-center text-xs"
                                title="Clear variable"
                              >
                                ×
                              </button>
                            )}
                          </div>

                          {/* Quick Insert Token Chips */}
                          <div className="flex flex-wrap items-center gap-1.5 pt-1">
                            <span className="text-[11px] text-text-muted font-medium mr-1">
                              Insert field:
                            </span>
                            {TOKEN_OPTIONS.map(({ token, label }) => {
                              const isSelected = currentVal === token;
                              return (
                                <button
                                  key={token}
                                  type="button"
                                  onClick={() =>
                                    setVariables((current) => ({
                                      ...current,
                                      [key]: isSelected ? '' : token,
                                    }))
                                  }
                                  className={`h-7 px-2.5 rounded-lg border text-xs font-medium transition-all flex items-center gap-1 ${
                                    isSelected
                                      ? 'border-primary bg-surface-mint text-primary font-bold shadow-xs'
                                      : 'border-border-default bg-white text-text-secondary hover:border-primary/60 hover:text-text-primary'
                                  }`}
                                >
                                  {label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* SECTION 4: Media Header URL (Conditional) */}
              {showHeaderUrl && (
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-semibold text-text-secondary">
                    {headerMediaKind(selectedTemplate) === 'video'
                      ? 'Upload video'
                      : headerMediaKind(selectedTemplate) === 'document'
                        ? 'Upload document'
                        : 'Upload image'}{' '}
                    <span className="text-primary">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="stage-header-media-upload"
                      type="file"
                      className="sr-only"
                      accept={
                        headerMediaKind(selectedTemplate) === 'image'
                          ? 'image/*'
                          : headerMediaKind(selectedTemplate) === 'video'
                            ? 'video/*'
                            : headerMediaKind(selectedTemplate) === 'document'
                              ? '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt'
                              : '*/*'
                      }
                      disabled={isUploadingHeader}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        e.target.value = '';
                        if (!file || !companyId) return;
                        setIsUploadingHeader(true);
                        const uploadToastId =
                          toast.loading('Uploading file...');
                        try {
                          const response = await uploadWhatsAppFlowMedia({
                            companyId,
                            file,
                          });
                          if (!response?.url) {
                            throw new Error('Invalid upload response');
                          }
                          setHeaderMediaUrl(response.url);
                          setHeaderMediaName(file.name);
                          toast.success('File uploaded.', {
                            id: uploadToastId,
                          });
                        } catch (err) {
                          console.error(err);
                          toast.error(
                            'Failed to upload file. Please try again.',
                            {
                              id: uploadToastId,
                            },
                          );
                        } finally {
                          setIsUploadingHeader(false);
                        }
                      }}
                    />
                    <label
                      htmlFor="stage-header-media-upload"
                      className={`inline-flex items-center px-4 py-2 rounded-lg border border-border-default text-sm font-semibold transition-all ${
                        isUploadingHeader
                          ? 'bg-form-background-light text-text-muted cursor-not-allowed opacity-50'
                          : 'bg-white text-main-black hover:bg-surface-mint cursor-pointer'
                      }`}
                    >
                      <span className="truncate max-w-[200px]">
                        {isUploadingHeader
                          ? 'Uploading...'
                          : headerMediaName || 'Choose file'}
                      </span>
                    </label>
                  </div>
                </div>
              )}

              {/* SECTION 5: Status Toggle */}
              <div className="bg-white rounded-2xl border border-border-default p-5 shadow-sm flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors ${
                      isActive
                        ? 'bg-surface-mint text-primary'
                        : 'bg-gray-100 text-gray-400'
                    }`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h5 className="text-sm font-bold text-text-primary">
                        Automation Status
                      </h5>
                      <span
                        className={`px-2 py-0.5 rounded-md text-[11px] font-bold ${
                          isActive
                            ? 'bg-surface-mint text-primary'
                            : 'bg-gray-100 text-text-muted'
                        }`}
                      >
                        {isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
                <Switch
                  checked={isActive}
                  onChange={setIsActive}
                  aria-label="Automation active toggle"
                />
              </div>
            </div>

            {/* RIGHT COLUMN: Live Preview */}
            <div
              className={`flex flex-col gap-4 ${mobileTab === 'form' ? 'hidden lg:flex' : 'flex'} lg:col-span-5 xl:col-span-5 lg:sticky lg:top-0`}
            >
              <div className="bg-white rounded-2xl border border-border-default shadow-sm p-4  flex flex-col gap-3">
                <div className="flex items-center justify-between pb-3 border-b border-border-default">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-[#25D366]/15 flex items-center justify-center text-[#25D366]">
                      <WhatsAppIcon className="w-4 h-4 text-[#25D366]" />
                    </div>
                    <div>
                      <h5 className="text-sm font-bold text-text-primary">
                        WhatsApp Live Preview
                      </h5>
                    </div>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-surface-mint text-primary">
                    Live
                  </span>
                </div>

                <div className="pipeline-phone-wrap flex justify-center -mb-[78px] sm:-mb-[96px]">
                  <TemplatePhonePreview
                    template={selectedTemplate || null}
                    templateValues={templateValues}
                    className="scale-[0.85] origin-top"
                  />
                </div>

                {/* Form Action Buttons */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-border-default">
                  <Button
                    variant="secondary"
                    onClick={showList}
                    disabled={saveMutation.isPending}
                    className="h-11 px-6 rounded-xl text-sm font-medium"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={
                      saveMutation.isPending ||
                      isReadOnly ||
                      (!editing && !stageId) ||
                      !templateKey
                    }
                    className="h-11 px-8 rounded-xl text-sm font-semibold shadow-sm"
                  >
                    {saveMutation.isPending ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Saving...
                      </span>
                    ) : editing ? (
                      'Save Changes'
                    ) : (
                      'Create Automation'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ----------------------------------------------------
  // VIEW: LOGS (VIEW AUTOMATION SEND LOGS)
  // ----------------------------------------------------
  if (view === 'logs') {
    const results = logsData?.results || [];
    const stageOptions = [
      { value: '', label: 'All stages' },
      ...stages.map((stage) => ({
        value: String(stage.id),
        label: stage.name,
      })),
    ];

    return (
      <div className="flex flex-col flex-1 min-h-0">
        {pageHeader}
        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar flex flex-col gap-4 pr-1">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 w-full shrink-0">
            <div className="flex flex-wrap items-center gap-2.5 flex-1">
              <CustomSelect
                value={logStatus}
                onChange={(value) => {
                  setLogStatus(value);
                  setLogPage(1);
                }}
                options={LOG_STATUSES}
                className="w-full sm:w-44 h-11"
                buttonClassName="w-full h-11 flex items-center justify-between px-3.5 rounded-xl bg-white border border-border-default text-text-primary text-sm font-medium"
                textSizeClass="text-sm"
              />
              {!logAutomationId && (
                <CustomSelect
                  value={logStageId}
                  onChange={(value) => {
                    setLogStageId(value);
                    setLogPage(1);
                  }}
                  options={stageOptions}
                  className="w-full sm:w-52 h-11"
                  buttonClassName="w-full h-11 flex items-center justify-between px-3.5 rounded-xl bg-white border border-border-default text-text-primary text-sm font-medium"
                  textSizeClass="text-sm"
                />
              )}
              {!logAutomationId && contactIdFromLead && (
                <TableChip tone="primary">
                  Contact #{contactIdFromLead}
                </TableChip>
              )}
            </div>

            {/* <Button
              variant="secondary"
              onClick={showList}
              className="h-11 px-5 rounded-xl text-sm font-medium shrink-0"
            >
              ← Back to Stage Triggers
            </Button> */}
          </div>

          <div className="w-full rounded-2xl bg-white border border-border-default shadow-sm">
            <DashboardTable>
              <DashboardTableHeader>
                <th className="py-3 px-4 text-left font-medium text-xs">
                  Contact
                </th>
                <th className="py-3 px-4 text-left font-medium text-xs">
                  Phone
                </th>
                <th className="py-3 px-4 text-left font-medium text-xs">
                  Stage
                </th>
                <th className="py-3 px-4 text-left font-medium text-xs">
                  Scheduled At
                </th>
                <th className="py-3 px-4 text-left font-medium text-xs">
                  Status
                </th>
                <th className="py-3 px-4 text-left font-medium text-xs">
                  Details / Error
                </th>
                <th className="py-3 px-4 text-right font-medium text-xs">
                  Action
                </th>
              </DashboardTableHeader>
              <DashboardTableBody>
                {logsLoading && !logsData ? (
                  [...Array(5)].map((_, i) => (
                    <DashboardTableRow key={`log-skel-${i}`}>
                      {[...Array(7)].map((__, cell) => (
                        <td key={cell} className="py-4 px-4">
                          <div className="h-4 w-24 bg-gray-100 rounded animate-pulse" />
                        </td>
                      ))}
                    </DashboardTableRow>
                  ))
                ) : results.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="py-16 text-center text-text-muted"
                    >
                      <div className="flex flex-col items-center justify-center gap-2">
                        <span className="text-sm font-semibold text-text-primary">
                          No automation logs yet
                        </span>
                        <span className="text-xs text-text-muted">
                          Messages scheduled when leads enter inquires stages
                          will appear here.
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  results.map((row) => (
                    <DashboardTableRow key={row.id}>
                      <td className="py-3.5 px-4 text-left">
                        <button
                          type="button"
                          className="text-primary font-semibold hover:underline text-sm"
                          onClick={() => {
                            try {
                              sessionStorage.setItem(
                                'crm-contact-overview-id',
                                String(row.contact_id),
                              );
                              sessionStorage.setItem(
                                'crm-contact-overview-name',
                                row.contact_name || '',
                              );
                            } catch {
                              // ignore storage errors
                            }
                            navigate('/crm-contacts/overview', {
                              state: {
                                contactId: row.contact_id,
                                contactName: row.contact_name,
                                from: '/crm-settings',
                              },
                            });
                          }}
                        >
                          {row.contact_name || `Lead #${row.contact_id}`}
                        </button>
                      </td>
                      <td className="py-3.5 px-4 text-left text-xs text-text-secondary font-mono">
                        {row.contact_phone || '—'}
                      </td>
                      <td className="py-3.5 px-4 text-left text-sm font-medium text-text-primary">
                        {row.stage_name || '—'}
                      </td>
                      <td className="py-3.5 px-4 text-left text-xs text-text-secondary">
                        {formatWhen(row.scheduled_at)}
                      </td>
                      <td className="py-3.5 px-4 text-left">
                        <TableChip tone={STATUS_TONE[row.status] || 'neutral'}>
                          {row.status}
                        </TableChip>
                      </td>
                      <td className="py-3.5 px-4 text-left text-xs text-text-secondary max-w-xs truncate">
                        {logReason(row)}
                      </td>
                      <td className="py-3.5 px-4 text-right whitespace-nowrap">
                        {row.status === 'pending' && !isReadOnly ? (
                          <div
                            className="flex items-center justify-end"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              title="Cancel send"
                              aria-label="Cancel send"
                              disabled={cancelMutation.isPending}
                              onClick={() => setPendingCancel(row)}
                              className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg hover:bg-yellow-500/10 transition-colors focus:outline-none disabled:opacity-50"
                            >
                              <img
                                src={pauseCircleIcon}
                                alt="Cancel send"
                                className="w-5 h-5 object-contain shrink-0"
                              />
                            </button>
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                    </DashboardTableRow>
                  ))
                )}
              </DashboardTableBody>
            </DashboardTable>
          </div>
          <Pagination
            page={logPage}
            totalCount={logsData?.count || 0}
            itemsPerPage={10}
            onChange={setLogPage}
          />
        </div>
        <StopFollowUpConfirmModal
          isOpen={Boolean(pendingCancel)}
          title="Cancel send"
          entityLabel="this scheduled template"
          description="This scheduled template will not be sent. The stage rule stays on for other leads."
          onCancel={() => {
            if (!cancelMutation.isPending) setPendingCancel(null);
          }}
          onConfirm={() => cancelMutation.mutate(pendingCancel.id)}
          isPending={cancelMutation.isPending}
        />
      </div>
    );
  }

  // ----------------------------------------------------
  // VIEW: LIST (AUTOMATIONS TABLE VIEW)
  // ----------------------------------------------------
  const loading = stagesLoading || automationsLoading;

  return (
    <div className="pipeline-automations flex flex-col flex-1 min-h-0">
      {pageHeader}

      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar flex flex-col gap-4 pb-8 pr-1">
        {/* Top Summary Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="px-5 py-4 rounded-2xl bg-white border border-[#E6EFE4] flex items-center justify-between">
            <div>
              <span className="text-sm text-[#868686] block">
                Inquiries Stages
              </span>
              <span className="text-[28px] leading-8 font-semibold text-[#1F2241] mt-1 block">
                {stages.length}
              </span>
            </div>
            <span className="w-10 h-10 rounded-xl bg-[#EBF4EA] text-primary flex items-center justify-center">
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="1.8"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 7h16M4 12h16M4 17h10"
                />
              </svg>
            </span>
          </div>
          <div className="px-5 py-4 rounded-2xl bg-white border border-[#E6EFE4] flex items-center justify-between">
            <div>
              <span className="text-sm text-[#868686] block">
                Active Stage Triggers
              </span>
              <span className="text-[28px] leading-8 font-semibold text-[#1F2241] mt-1 block">
                {activeAutomationsCount}
              </span>
            </div>
            <span className="w-10 h-10 rounded-xl bg-[#EBF4EA] text-primary flex items-center justify-center">
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="1.8"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13 3L5 14h7l-1 7 8-11h-7l1-7z"
                />
              </svg>
            </span>
          </div>
          <div className="px-5 py-4 rounded-2xl bg-white border border-[#E6EFE4] flex items-center justify-between">
            <div>
              <span className="text-sm text-[#868686] block">Unconfigured</span>
              <span className="text-[28px] leading-8 font-semibold text-[#1F2241] mt-1 block">
                {Math.max(0, stages.length - automations.length)}
              </span>
            </div>
            <span className="w-10 h-10 rounded-xl bg-[#EEF3FF] text-[#4C6FFF] flex items-center justify-center">
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="1.8"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01"
                />
              </svg>
            </span>
          </div>
        </div>

        {/* Action Header & Filter Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 w-full shrink-0">
          <div className="relative w-full sm:w-72">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by stage or template..."
              className="w-full h-11 pl-9 pr-4 rounded-xl bg-white border border-border-default text-text-primary text-sm placeholder:text-text-muted focus:outline-none focus:border-primary transition-colors"
            />
            <svg
              className="w-4 h-4 text-text-muted absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>

          <div className="flex items-center gap-2.5 w-full sm:w-auto">
            <Button
              variant="secondary"
              onClick={() => openLogs(null)}
              className="h-11 px-4 rounded-xl text-sm font-medium flex-1 sm:flex-none"
            >
              All Send Logs
            </Button>
            {canCreate && (
              <Button
                variant="primary"
                onClick={() => openCreate()}
                disabled={createStageOptions.length === 0 || isReadOnly}
                className="h-11 px-5 rounded-xl text-sm font-semibold flex-1 sm:flex-none shadow-sm"
                icon={<PlusIcon className="w-4 h-4" />}
              >
                <span>Add Automation</span>
              </Button>
            )}
          </div>
        </div>

        <div className="w-full min-w-0">
          <V2Table
            storageKey="inquiry_stage_triggers_column_widths"
            columns={[
              {
                id: 'stage',
                label: 'Stage',
                defaultWidth: 200,
                minWidth: 140,
                cell: ({ stage }) => (
                  <div className="flex items-center gap-2.5">
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: stage.color || '#3b82f6' }}
                    />
                    <span className="font-semibold text-text-primary text-sm truncate">
                      {stage.name}
                    </span>
                  </div>
                ),
              },
              {
                id: 'template',
                label: 'WhatsApp Template',
                defaultWidth: 220,
                minWidth: 140,
                cell: ({ rule }) =>
                  rule ? (
                    <span className="text-sm text-[#1F2241] truncate block">
                      {rule.template_name}
                    </span>
                  ) : (
                    <span className="text-sm text-[#9AA0A6]">
                      Not configured
                    </span>
                  ),
              },
              {
                id: 'delay',
                label: 'Delivery Delay',
                defaultWidth: 150,
                minWidth: 110,
                cell: ({ rule }) => (rule ? formatDelay(rule) : '—'),
              },
              {
                id: 'status',
                label: 'Active Status',
                defaultWidth: 160,
                minWidth: 130,
                stopPropagation: true,
                cell: ({ stage, rule }) =>
                  rule ? (
                    <div className="flex items-center gap-2.5">
                      <Switch
                        checked={Boolean(rule.is_active)}
                        disabled={
                          isReadOnly || !canUpdate || toggleMutation.isPending
                        }
                        aria-label={`Toggle ${stage.name}`}
                        onChange={(next) => {
                          if (!next) setPendingToggleOff(rule);
                          else {
                            toggleMutation.mutate({
                              automationId: rule.id,
                              nextActive: true,
                            });
                          }
                        }}
                      />
                      <span
                        className={`text-xs font-semibold ${
                          rule.is_active ? 'text-primary' : 'text-text-muted'
                        }`}
                      >
                        {rule.is_active ? 'Active' : 'Paused'}
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs text-text-muted">—</span>
                  ),
              },
              {
                id: 'actions',
                label: 'Actions',
                defaultWidth: 420,
                minWidth: 160,
                stopPropagation: true,
                cell: ({ stage, rule }) => (
                  <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                    {rule ? (
                      <>
                        <button
                          type="button"
                          title="View send logs"
                          className="h-9 px-3 rounded-lg border border-border-default bg-white text-sm font-medium text-text-primary hover:border-primary shrink-0"
                          onClick={() => openLogs(rule)}
                        >
                          Logs
                        </button>
                        <button
                          type="button"
                          title="Send to existing leads"
                          className="h-9 px-3 rounded-lg border border-primary bg-white text-sm font-medium text-primary hover:bg-surface-mint shrink-0"
                          onClick={() => {
                            setApplyNotice('');
                            setApplyRunning(false);
                            setApplyRule({
                              id: rule.id,
                              stage_name: rule.stage_name || stage.name,
                              is_active: Boolean(rule.is_active),
                            });
                          }}
                        >
                          Send to existing leads
                        </button>
                        {canUpdate && (
                          <button
                            type="button"
                            title="Edit automation"
                            onClick={() => openEdit(rule)}
                            className="w-9 h-9 rounded-lg border border-border-default bg-white flex items-center justify-center hover:bg-surface-mint transition-colors shrink-0"
                          >
                            <img
                              src={editIcon}
                              alt="Edit"
                              className="w-4 h-4"
                            />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            title="Delete automation"
                            onClick={() => setPendingDelete(rule)}
                            className="w-9 h-9 rounded-lg border border-border-default bg-white flex items-center justify-center hover:bg-red-50 transition-colors shrink-0"
                          >
                            <img
                              src={trashIcon}
                              alt="Delete"
                              className="w-4 h-4"
                            />
                          </button>
                        )}
                      </>
                    ) : (
                      canCreate &&
                      !isReadOnly && (
                        <button
                          type="button"
                          onClick={() => openCreate(stage.id)}
                          className="h-9 px-4 rounded-lg border border-primary bg-white text-primary text-sm font-medium hover:bg-surface-mint transition-colors"
                        >
                          Configure
                        </button>
                      )
                    )}
                  </div>
                ),
              },
            ]}
            data={filteredRows}
            rowKey={({ stage }) => stage.id}
            loading={loading}
            loadingRowCount={5}
            empty={
              <div className="flex flex-col items-center gap-2">
                <span className="text-sm font-semibold text-text-primary">
                  {searchQuery
                    ? 'No matching stages or triggers'
                    : 'No inquiry stages found'}
                </span>
                <span className="text-xs text-text-muted">
                  {searchQuery
                    ? 'Try searching with a different term.'
                    : 'Set up CRM inquiry stages to configure automated messaging.'}
                </span>
              </div>
            }
          />
        </div>
      </div>

      {/* Confirmation Modals */}
      <Modal
        isOpen={Boolean(applyRule)}
        onClose={() => {
          if (applyMutation.isPending) return;
          setApplyRule(null);
          setApplyNotice('');
        }}
        title="Send to people already here?"
        // description="New leads get this WhatsApp message automatically when they enter the stage. This choice is only for people who are already in it."
        size="lg"
      >
        {applyPreviewLoading ? (
          <p className="text-sm text-text-muted">
            Checking who is in this stage...
          </p>
        ) : applyPreviewError ? (
          <p className="text-sm text-status-error">
            {parseApiError(applyPreviewError).message}
          </p>
        ) : applyPreview ? (
          <ApplyExistingBody
            preview={applyPreview}
            stageName={applyRule?.stage_name || 'this stage'}
            isActive={Boolean(applyRule?.is_active)}
            notice={applyNotice}
            running={applyRunning || Boolean(applyPreview.apply_in_progress)}
            pending={applyMutation.isPending}
            onSkip={() => {
              setApplyRule(null);
              setApplyNotice('');
            }}
            onSend={() => applyMutation.mutate()}
          />
        ) : null}
      </Modal>
      <ConfirmDeleteModal
        isOpen={Boolean(pendingDelete)}
        title="Delete Stage Automation"
        description="Pending rows for this rule are removed with it."
        itemName={pendingDelete?.stage_name}
        isPending={deleteMutation.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => deleteMutation.mutate(pendingDelete.id)}
      />
      <ConfirmDeleteModal
        isOpen={Boolean(pendingToggleOff)}
        title="Turn Off Automation"
        description="Turning this off cancels every queued send for this rule. The rule schedules nothing while it is off."
        itemName={pendingToggleOff?.stage_name}
        confirmText="Turn Off"
        isPending={toggleMutation.isPending}
        onCancel={() => setPendingToggleOff(null)}
        onConfirm={() =>
          toggleMutation.mutate({
            automationId: pendingToggleOff.id,
            nextActive: false,
          })
        }
      />
    </div>
  );
}
